// CLI v0.8 — `cloudgrid dev` port allocator. Per backlog 096 layer 1.
//
// Three behaviors live here.
//
// 1. With a `hint` (previous lock's port): try the hint → on EADDRINUSE
//    fall back to ephemeral (or surface `CloudGridError` with `{ fixed: true }`,
//    driven by `--fixed-ports`).
// 2. Without a hint BUT with a `defaultHint` (convention default for the
//    service type — see `dev-default-ports.ts`): try the default → on
//    EADDRINUSE linear-scan +1..+LINEAR_SCAN_MAX → if the whole window
//    is taken, fall through to ephemeral. `--fixed-ports` does not apply
//    to the default-hint path: the default is a convenience, not a contract.
// 3. Without any hint: bind to port 0 and return the OS-assigned ephemeral
//    port — same shape as the previous inline helper.
//
// The hint is always treated as a courtesy: if the port is grabbed by
// another process between dev runs the default path keeps the dev runner
// usable. Strict reuse is opt-in.

import { createServer } from 'node:net';
import { CloudGridError } from '../utils.js';

/** Linear-scan window when a convention-default port is taken. */
const LINEAR_SCAN_MAX = 20;

export interface FindFreePortOptions {
  /** When `true`, fail with `CloudGridError` if `hint` is unavailable. */
  fixed?: boolean;
  /**
   * Convention default for the service type (e.g. 3000 for nextjs).
   * Only consulted when `hint` is undefined. On EADDRINUSE the allocator
   * linear-scans +1..+{@link LINEAR_SCAN_MAX}; if the whole window is
   * taken, falls through to ephemeral. Does not interact with `fixed`.
   */
  defaultHint?: number;
}

/**
 * Allocate a TCP port. With `hint`, try that port first; on `EADDRINUSE`
 * fall back to ephemeral (or throw, with `{ fixed: true }`). Without
 * `hint` but with `defaultHint`, try the default then linear-scan +1..+20
 * before falling through to ephemeral. Without either, return an
 * OS-assigned ephemeral port.
 */
export function findFreePort(
  hint?: number,
  opts: FindFreePortOptions = {},
): Promise<number> {
  if (typeof hint === 'number' && hint > 0) {
    return tryBind(hint).catch((err) => {
      if (isAddrInUse(err)) {
        if (opts.fixed) {
          throw new CloudGridError(
            `Port ${hint} is already in use and --fixed-ports was set. Free the port or drop --fixed-ports.`,
          );
        }
        return tryBind(0);
      }
      throw err;
    });
  }
  if (typeof opts.defaultHint === 'number' && opts.defaultHint > 0) {
    return tryBindOrNext(opts.defaultHint, opts.defaultHint + LINEAR_SCAN_MAX);
  }
  return tryBind(0);
}

/**
 * Try `port`; on EADDRINUSE walk +1 until `maxInclusive` is exhausted.
 * Falls through to ephemeral (`port 0`) when the whole window is taken.
 * Non-EADDRINUSE errors propagate.
 */
function tryBindOrNext(port: number, maxInclusive: number): Promise<number> {
  return tryBind(port).catch((err) => {
    if (!isAddrInUse(err)) throw err;
    if (port >= maxInclusive) return tryBind(0);
    return tryBindOrNext(port + 1, maxInclusive);
  });
}

function tryBind(port: number): Promise<number> {
  return new Promise<number>((resolve, reject) => {
    const srv = createServer();
    const onError = (err: NodeJS.ErrnoException) => {
      srv.removeListener('listening', onListening);
      reject(err);
    };
    const onListening = () => {
      srv.removeListener('error', onError);
      const addr = srv.address();
      if (!addr || typeof addr === 'string') {
        srv.close();
        reject(new Error('Unexpected address from net.createServer'));
        return;
      }
      const bound = addr.port;
      srv.close(() => resolve(bound));
    };
    srv.once('error', onError);
    srv.once('listening', onListening);
    srv.listen(port, '127.0.0.1');
  });
}

function isAddrInUse(err: unknown): boolean {
  return Boolean(err) && (err as NodeJS.ErrnoException).code === 'EADDRINUSE';
}
